import * as React from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "next-intl";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Eye, Lock, Unlock } from "lucide-react";
import {
  FilterSearch,
  FilterSelect,
  FiltersToolbar,
  UserStatusBadge,
  useToast,
} from "@chotca/ui";
import {
  ACCOUNT_TYPE_LABEL_VI,
  ORG_ROLE_LABEL_VI,
  USER_STATUS_LABEL_VI,
  type AccountType,
  type OrgRole,
} from "@chotca/config";
import { useAdminUsers } from "@chotca/api-client";
import { useTableFilters } from "@/lib/use-table-filters";
import { getSdk } from "@chotca/api-client";
import {
  DataTable,
  usePagination,
  type BulkAction,
  type Column,
  type RowAction,
} from "@/components/data-table";

type Membership = {
  orgId: string;
  orgName: string;
  role: OrgRole;
  joinedAt: string;
};

type UserRow = {
  id: string;
  phone: string | null;
  email: string | null;
  globalRole: string;
  accountType: AccountType;
  status: string;
  displayName: string | null;
  workerKycVerifiedAt?: string | null;
  memberships: Membership[];
};

const ACCOUNT_TYPE_TONE: Record<AccountType, string> = {
  WORKER: "bg-sky-100 text-sky-800",
  EMPLOYER: "bg-violet-100 text-violet-800",
  ADMIN: "bg-amber-100 text-amber-900",
};

function AccountTypeBadge({ type }: { type: AccountType }) {
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-bold ${ACCOUNT_TYPE_TONE[type]}`}
    >
      {ACCOUNT_TYPE_LABEL_VI[type]}
    </span>
  );
}

export function AccountsClient() {
  const t = useTranslations("admin");
  const nav = useNavigate();
  const filters = useTableFilters({
    search: { debounce: 300 },
    status: {},
    accountType: {},
  });
  const { page, pageSize, setPage, setPageSize } = usePagination();
  const { data, isLoading, isFetching } = useAdminUsers({
    search: filters.applied.search,
    status: filters.applied.status,
    accountType: filters.applied.accountType,
    page,
    limit: pageSize,
  });
  const qc = useQueryClient();
  const { toast } = useToast();
  const [pendingId, setPendingId] = React.useState<string | null>(null);
  const [bulkPending, setBulkPending] = React.useState(false);

  function refresh() {
    qc.invalidateQueries({ queryKey: ["admin", "users"] });
  }

  const sdk = () => getSdk();
  const suspend = useMutation({
    mutationFn: (id: string) => sdk().admin.suspendUser(id),
    onMutate: (id) => setPendingId(id),
    onSettled: () => setPendingId(null),
    onSuccess: () => {
      refresh();
      toast({ tone: "success", title: "Đã khoá người dùng" });
    },
  });
  const activate = useMutation({
    mutationFn: (id: string) => sdk().admin.activateUser(id),
    onMutate: (id) => setPendingId(id),
    onSettled: () => setPendingId(null),
    onSuccess: () => {
      refresh();
      toast({ tone: "success", title: "Đã mở khoá" });
    },
  });
  async function bulk(
    rows: UserRow[],
    fn: (id: string) => Promise<unknown>,
    successMsg: string,
  ) {
    setBulkPending(true);
    try {
      await Promise.all(rows.map((r) => fn(r.id)));
      toast({ tone: "success", title: `${successMsg} (${rows.length})` });
      refresh();
    } catch {
      toast({ tone: "error", title: "Có lỗi xảy ra" });
    } finally {
      setBulkPending(false);
    }
  }

  const list = (data?.items ?? []) as UserRow[];

  const columns: Column<UserRow>[] = [
    {
      key: "name",
      header: "Tên",
      cell: (u) => (
        <div className="flex flex-col">
          <span className="font-semibold text-ink">{u.displayName ?? "—"}</span>
          <span className="text-[11px] text-ash">{u.id.slice(0, 8)}</span>
        </div>
      ),
    },
    {
      key: "type",
      header: "Loại tài khoản",
      cell: (u) => (
        <div className="flex flex-col gap-1">
          <AccountTypeBadge type={u.accountType} />
          {u.accountType === "EMPLOYER" && u.memberships.length > 0 ? (
            <span className="text-[11px] text-ash">
              {u.memberships
                .map((m) => ORG_ROLE_LABEL_VI[m.role] ?? m.role)
                .join(", ")}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: "phone",
      header: "Phone",
      cell: (u) => <span className="font-mono text-xs">{u.phone ?? "—"}</span>,
    },
    {
      key: "email",
      header: "Email",
      cell: (u) => <span className="text-xs">{u.email ?? "—"}</span>,
    },
    {
      key: "status",
      header: "Trạng thái",
      cell: (u) => <UserStatusBadge status={u.status as "ACTIVE"} />,
    },
    {
      key: "kyc",
      header: "KYC",
      cell: (u) =>
        u.workerKycVerifiedAt ? (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-800">
            ✓
          </span>
        ) : (
          <span className="text-xs text-ash">—</span>
        ),
    },
  ];

  const rowActions: RowAction<UserRow>[] = [
    {
      key: "view",
      label: "Xem chi tiết tài khoản",
      icon: Eye,
      onClick: (u) => nav(`/accounts/${u.id}`),
    },
    {
      key: "suspend",
      label: "Khoá tài khoản",
      icon: Lock,
      onClick: (u) => suspend.mutate(u.id),
      show: (u) => u.status === "ACTIVE",
      destructive: true,
      loading: (u) => suspend.isPending && pendingId === u.id,
    },
    {
      key: "activate",
      label: "Mở khoá tài khoản",
      icon: Unlock,
      onClick: (u) => activate.mutate(u.id),
      show: (u) => u.status !== "ACTIVE",
      loading: (u) => activate.isPending && pendingId === u.id,
    },
  ];

  const bulkActions: BulkAction<UserRow>[] = [
    {
      key: "suspend",
      label: "Khoá",
      icon: Lock,
      destructive: true,
      onClick: (rows) =>
        bulk(
          rows.filter((r) => r.status === "ACTIVE"),
          (id) => sdk().admin.suspendUser(id),
          "Đã khoá",
        ),
      loading: () => bulkPending,
    },
    {
      key: "activate",
      label: "Mở khoá",
      icon: Unlock,
      onClick: (rows) =>
        bulk(
          rows.filter((r) => r.status !== "ACTIVE"),
          (id) => sdk().admin.activateUser(id),
          "Đã mở khoá",
        ),
      loading: () => bulkPending,
    },
  ];

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-ink">{t("accountsTitle")}</h1>
        <Link
          to="/kyc/workers"
          className="text-xs font-semibold text-ash hover:text-ink"
        >
          KYC pending →
        </Link>
      </header>

      <FiltersToolbar
        hasActiveFilters={filters.hasAny}
        onClearAll={filters.clear}
      >
        <FilterSearch
          value={filters.values.search ?? ""}
          onChange={(v) => filters.set("search", v)}
          placeholder="Tìm theo phone hoặc email..."
        />
        <FilterSelect
          value={filters.values.accountType ?? ""}
          onValueChange={(v) => filters.set("accountType", v)}
          placeholder="Mọi loại tài khoản"
          options={[
            { value: "WORKER", label: ACCOUNT_TYPE_LABEL_VI.WORKER },
            { value: "EMPLOYER", label: ACCOUNT_TYPE_LABEL_VI.EMPLOYER },
            { value: "ADMIN", label: ACCOUNT_TYPE_LABEL_VI.ADMIN },
          ]}
        />
        <FilterSelect
          value={filters.values.status ?? ""}
          onValueChange={(v) => filters.set("status", v)}
          placeholder="Mọi trạng thái"
          options={[
            { value: "ACTIVE", label: USER_STATUS_LABEL_VI.ACTIVE },
            { value: "SUSPENDED", label: USER_STATUS_LABEL_VI.SUSPENDED },
            { value: "DELETED", label: USER_STATUS_LABEL_VI.DELETED },
          ]}
        />
      </FiltersToolbar>

      <DataTable
        columns={columns}
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        rowKey={(u) => u.id}
        rowActions={rowActions}
        bulkActions={bulkActions}
        onRowClick={(u) => nav(`/accounts/${u.id}`)}
        page={page}
        pageSize={pageSize}
        total={data?.total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}
